import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Skeleton,
  Divider,
  ListItemIcon,
  Typography,
} from "@mui/material";
import {
  Language as LanguageIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ManageAccounts as ManageAccountsIcon,
  VideoLibrary as VideoLibraryIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ThemeMode } from "../../hooks/useThemeMode";

type SupportedLanguage = "de" | "en";

type HeaderProps = {
  mode: ThemeMode;
  onToggleTheme: () => void;
  onLanguageChange: (lang: SupportedLanguage) => void;
};

const brandLogoUrl = new URL("../../assets/logo-rgb.png", import.meta.url).href;

const Header: React.FC<HeaderProps> = ({
  mode,
  onToggleTheme,
  onLanguageChange,
}) => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const fromState = React.useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    }),
    [location],
  );
  const [languageAnchor, setLanguageAnchor] =
    React.useState<null | HTMLElement>(null);
  const [navAnchor, setNavAnchor] = React.useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  const accountLabel = t("nav.account", { defaultValue: "Account" });

  const userDisplayName = React.useMemo(() => {
    if (!auth.user) return accountLabel;
    const username =
      typeof auth.user.username === "string" ? auth.user.username : undefined;
    const name =
      typeof auth.user.name === "string" ? auth.user.name : undefined;
    const email =
      typeof auth.user.email === "string" ? auth.user.email : undefined;
    return username || name || email || accountLabel;
  }, [accountLabel, auth.user]);

  const userEmail = React.useMemo(
    () =>
      auth.user && typeof auth.user.email === "string"
        ? auth.user.email
        : undefined,
    [auth.user],
  );

  const userInitial = React.useMemo(() => {
    const source = userDisplayName || userEmail || accountLabel;
    const initial = source?.trim()?.charAt(0);
    return initial ? initial.toUpperCase() : "U";
  }, [accountLabel, userDisplayName, userEmail]);

  const avatarElement = (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        fontSize: 16,
        bgcolor: "primary.light",
        color: "primary.contrastText",
      }}
    >
      {userInitial}
    </Avatar>
  );

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    setUserMenuAnchor(null);
    setNavAnchor(null);
    void auth.logout();
  };

  const handleLanguageSelect = (lang: SupportedLanguage) => {
    onLanguageChange(lang);
    setLanguageAnchor(null);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundImage: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        backgroundColor: "transparent",
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
        <Toolbar
          sx={{
            display: "flex",
            gap: 2,
            px: 0,
            minHeight: { xs: 72, md: 96 },
            py: { xs: 0.5, md: 1.25 },
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.45)"
                    : "rgba(255,255,255,0.8)",
                border: "1.5px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.35)"
                    : "rgba(0,0,0,0.2)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
                backdropFilter: "blur(10px) saturate(160%)",
              }}
            >
              <Box
                component="img"
                src={brandLogoUrl}
                alt="SQUASHPaT"
                sx={{
                  height: { xs: 72, md: 88 },
                  display: "block",
                  filter: (theme) =>
                    theme.palette.mode === "dark"
                      ? "drop-shadow(0 2px 2px rgba(0,0,0,0.7)) drop-shadow(0 6px 12px rgba(0,0,0,0.45))"
                      : "drop-shadow(0 1px 1px rgba(0,0,0,0.25)) drop-shadow(0 6px 10px rgba(0,0,0,0.2))",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 1,
              alignItems: "center",
            }}
          >
            <Button color="inherit" component={RouterLink} to="/">
              {t("nav.home")}
            </Button>
            <Button color="inherit" component={RouterLink} to="/videos">
              {t("nav.videos")}
            </Button>
            <Button color="inherit" component={RouterLink} to="/submit">
              {t("nav.submit")}
            </Button>
            <Button color="inherit" component={RouterLink} to="/kontakt">
              {t("nav.contact")}
            </Button>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {!auth.initialized && (
              <>
                <Skeleton
                  variant="rounded"
                  width={120}
                  height={40}
                  sx={{ display: { xs: "none", md: "block" } }}
                />
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ display: { xs: "block", md: "none" } }}
                />
              </>
            )}

            {auth.initialized && !auth.user && (
              <Button
                color="inherit"
                component={RouterLink}
                to="/login"
                state={{ from: fromState }}
                sx={{ display: { xs: "none", md: "inline-flex" } }}
              >
                {t("nav.login")}
              </Button>
            )}

            {auth.user && (
              <>
                <Tooltip title={userDisplayName}>
                  <Button
                    color="inherit"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(userMenuAnchor)}
                    aria-controls={userMenuAnchor ? 'account-menu' : undefined}
                    onClick={handleUserMenuOpen}
                    startIcon={avatarElement}
                    sx={{
                      display: { xs: "none", md: "inline-flex" },
                      textTransform: "none",
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.75,
                      gap: 1,
                    }}
                  >
                    {userDisplayName}
                  </Button>
                </Tooltip>

                <Tooltip title={userDisplayName}>
                  <IconButton
                    color="inherit"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(userMenuAnchor)}
                    aria-controls={userMenuAnchor ? 'account-menu' : undefined}
                    onClick={handleUserMenuOpen}
                    sx={{ display: { xs: "inline-flex", md: "none" } }}
                  >
                    {avatarElement}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          <IconButton
            color="inherit"
            aria-label="toggle theme"
            onClick={onToggleTheme}
          >
            {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="language"
            aria-haspopup="menu"
            aria-expanded={Boolean(languageAnchor)}
            aria-controls={languageAnchor ? 'language-menu' : undefined}
            onClick={(event) => setLanguageAnchor(event.currentTarget)}
          >
            <LanguageIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="menu"
            aria-haspopup="menu"
            aria-expanded={Boolean(navAnchor)}
            aria-controls={navAnchor ? 'navigation-menu' : undefined}
            onClick={(event) => setNavAnchor(event.currentTarget)}
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Menu
            id="navigation-menu"
            anchorEl={navAnchor}
            open={Boolean(navAnchor)}
            onClose={() => setNavAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              component={RouterLink}
              to="/"
              onClick={() => setNavAnchor(null)}
            >
              {t("nav.home")}
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/videos"
              onClick={() => setNavAnchor(null)}
            >
              {t("nav.videos")}
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/submit"
              onClick={() => setNavAnchor(null)}
            >
              {t("nav.submit")}
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/kontakt"
              onClick={() => setNavAnchor(null)}
            >
              {t("nav.contact")}
            </MenuItem>
            {auth.initialized && !auth.user && (
              <MenuItem
                component={RouterLink}
                to="/login"
                state={{ from: fromState }}
                onClick={() => setNavAnchor(null)}
              >
                {t("nav.login")}
              </MenuItem>
            )}
            {auth.user && (
              <>
                <MenuItem
                  disabled
                  sx={{ cursor: "default", opacity: 1, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                      }}
                    >
                      {userInitial}
                    </Avatar>
                  </ListItemIcon>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      lineHeight={1.2}
                    >
                      {userDisplayName}
                    </Typography>
                    {userEmail && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        lineHeight={1.2}
                      >
                        {userEmail}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  component={RouterLink}
                  to="/admin/suggestions"
                  onClick={() => setNavAnchor(null)}
                >
                  <ListItemIcon>
                    <ManageAccountsIcon fontSize="small" />
                  </ListItemIcon>
                  {t("nav.admin")}
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/admin/series"
                  onClick={() => setNavAnchor(null)}
                >
                  <ListItemIcon>
                    <VideoLibraryIcon fontSize="small" />
                  </ListItemIcon>
                  {t("nav.series")}
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  {t("nav.logout")}
                </MenuItem>
              </>
            )}
          </Menu>

          <Menu
            id="account-menu"
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: 18,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                }}
              >
                {userInitial}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {userDisplayName}
                </Typography>
                {userEmail && (
                  <Typography variant="caption" color="text.secondary">
                    {userEmail}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              component={RouterLink}
              to="/admin/suggestions"
              onClick={() => {
                handleUserMenuClose();
              }}
            >
              <ListItemIcon>
                <ManageAccountsIcon fontSize="small" />
              </ListItemIcon>
              {t("nav.admin")}
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/admin/series"
              onClick={() => {
                handleUserMenuClose();
              }}
            >
              <ListItemIcon>
                <VideoLibraryIcon fontSize="small" />
              </ListItemIcon>
              {t("nav.series")}
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t("nav.logout")}
            </MenuItem>
          </Menu>

          <Menu
            id="language-menu"
            anchorEl={languageAnchor}
            open={Boolean(languageAnchor)}
            onClose={() => setLanguageAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              selected={i18n.language === "de"}
              onClick={() => handleLanguageSelect("de")}
            >
              Deutsch
            </MenuItem>
            <MenuItem
              selected={i18n.language === "en"}
              onClick={() => handleLanguageSelect("en")}
            >
              English
            </MenuItem>
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
